import { useEffect, useRef, useState } from "react";
import { Bell, Check } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";

/**
 * NotificationCenter — bell + dropdown panel.
 *
 * v3: notifications are seeded from local activity (last article published,
 * last message received, deploy stamp). We store "seen" IDs in localStorage
 * so the badge only counts unread ones. No backend push channel yet —
 * this is the UI surface we can wire live events into later.
 */

const SEEN_KEY = "stoicos.admin.notifications.seen";

function readSeen() {
  try { return JSON.parse(localStorage.getItem(SEEN_KEY) || "[]"); } catch { return []; }
}
function writeSeen(ids) {
  try { localStorage.setItem(SEEN_KEY, JSON.stringify(ids)); } catch { /* ignore */ }
}

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

/**
 * Derive notifications from raw activity events.
 * kind → label pattern (per spec §8).
 */
function eventsToNotifications(events, t) {
  const map = {
    article_published: { tint: "bg-emerald-500/70", label: t("admin.notif.published") },
    article_updated:   { tint: "bg-gold/70",        label: t("admin.notif.updated") },
    autoseo_import:    { tint: "bg-gold/70",        label: t("admin.notif.autoseo") },
    contact_message:   { tint: "bg-sky-400/70",     label: t("admin.notif.message") },
    system:            { tint: "bg-white/40",       label: t("admin.notif.system") },
  };
  return (events || []).slice(0, 12).map((e, i) => {
    const meta = map[e.kind] || map.system;
    return {
      id: `${e.when}::${e.kind}::${i}`,
      when: e.when,
      title: meta.label,
      body: e.description,
      tint: meta.tint,
    };
  });
}

export default function NotificationCenter({ events = [], testId = "notification-center" }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(() => readSeen());
  const wrapRef = useRef(null);

  const notifications = eventsToNotifications(events, t);
  const unread = notifications.filter((n) => !seen.includes(n.id));
  const unreadCount = unread.length;

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, []);

  const markAllRead = () => {
    const ids = notifications.map((n) => n.id);
    const next = Array.from(new Set([...seen, ...ids]));
    setSeen(next);
    writeSeen(next);
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="notification-bell"
        aria-label={t("admin.notif.title")}
        className="relative w-9 h-9 border border-gold/25 flex items-center justify-center hover:border-gold/50 transition-colors"
      >
        <Bell size={14} strokeWidth={1.5} className="text-gold" />
        {unreadCount > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-medium flex items-center justify-center"
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-panel"
          className="absolute right-0 mt-3 w-[320px] md:w-[380px] max-h-[70vh] overflow-auto bg-[#0a0a0a] border border-gold/25 shadow-2xl z-[70]"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
            <span className="text-[10px] tracking-[0.28em] uppercase text-gold">
              {t("admin.notif.title")}
            </span>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                data-testid="notification-mark-all"
                className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-bone/60 hover:text-gold transition-colors"
              >
                <Check size={12} strokeWidth={1.75} /> {t("admin.action.markAllRead")}
              </button>
            )}
          </div>

          {notifications.length === 0 && (
            <p className="text-sm text-bone/50 px-4 py-6">{t("admin.notif.none")}</p>
          )}
          <ul>
            {notifications.map((n) => {
              const isUnread = !seen.includes(n.id);
              return (
                <li
                  key={n.id}
                  className={`px-4 py-3 border-b border-white/5 last:border-b-0 ${isUnread ? "bg-white/[0.03]" : ""}`}
                >
                  <div className="flex items-start gap-3">
                    <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${n.tint}`} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-[13px] text-bone/90 font-medium truncate">{n.title}</p>
                        <span className="text-[10px] text-bone/40">{timeAgo(n.when)}</span>
                      </div>
                      <p className="mt-0.5 text-[12px] text-bone/60 leading-snug">{n.body}</p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
